import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { getCurrentLocation, LocationData, isGeolocationSupported } from '@/lib/geolocation';

const GeolocationTest = () => {
  const [location, setLocation] = useState<LocationData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    setIsSupported(isGeolocationSupported());
  }, []);

  const handleGetLocation = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const userLocation = await getCurrentLocation();
      setLocation(userLocation);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearLocation = () => {
    setLocation(null);
    setError(null);
  };

  return (
    <div className="container mx-auto p-6 max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle>Geolocation Service Test</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              This page tests the geolocation service to ensure it's working correctly.
            </p>
            
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Geolocation Support:</span>
              <span className={`px-2 py-1 rounded text-xs ${
                isSupported ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {isSupported ? 'Supported' : 'Not Supported'}
              </span>
            </div>
          </div>

          <div className="flex gap-2">
            <Button 
              onClick={handleGetLocation} 
              disabled={isLoading || !isSupported}
              className="flex-1"
            >
              {isLoading ? 'Getting Location...' : 'Get My Location'}
            </Button>
            
            {location && (
              <Button 
                onClick={handleClearLocation} 
                variant="outline"
                className="flex-1"
              >
                Clear Location
              </Button>
            )}
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">
                <strong>Error:</strong> {error}
              </p>
            </div>
          )}

          {location && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-2">
              <h3 className="font-medium text-green-800">Location Detected:</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="font-medium">Country:</span> {location.country}
                </div>
                <div>
                  <span className="font-medium">City:</span> {location.city}
                </div>
                <div>
                  <span className="font-medium">Timezone:</span> {location.timezone}
                </div>
                <div>
                  <span className="font-medium">Coordinates:</span> {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
                </div>
                {location.accuracy && (
                  <div>
                    <span className="font-medium">Accuracy:</span> ±{location.accuracy.toFixed(0)}m
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="text-xs text-muted-foreground space-y-1">
            <p><strong>How it works:</strong></p>
            <ol className="list-decimal list-inside space-y-1 ml-2">
              <li>First tries browser geolocation API (most accurate)</li>
              <li>Falls back to IP-based geolocation if browser fails</li>
              <li>Uses default Latvia/Riga location as final fallback</li>
              <li>Location is cached for 30 minutes to avoid repeated requests</li>
            </ol>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GeolocationTest;
